import { createTheme, responsiveFontSizes } from "@mui/material/styles";

export const theme = responsiveFontSizes(
  createTheme({
    root: {
      flexGrow: 1,
    },
    typography: {
      fontFamily: ["Outfit", "sans-serif"].join(","),
      htmlFontSize: 16,
      fontSize: 15,
      fontWeightLight: 400,
      fontWeightRegular: 500,
      fontWeightMedium: 600,
      fontWeightBold: 700,
      h1: {
        fontSize: "2.5rem",
        fontWeight: 800,
        lineHeight: 1.15,
        letterSpacing: "-0.03em",
      },
      h2: {
        fontSize: "2rem",
        fontWeight: 800,
        lineHeight: 1.18,
        letterSpacing: "-0.02em",
      },
      h3: {
        fontSize: "1.7rem",
        fontWeight: 700,
        lineHeight: 1.22,
        letterSpacing: "-0.02em",
      },
      h4: {
        fontSize: "1.4rem",
        fontWeight: 700,
        lineHeight: 1.28,
      },
      h5: {
        fontSize: "1.18rem",
        fontWeight: 700,
        lineHeight: 1.32,
      },
      h6: {
        fontSize: "1rem",
        fontWeight: 700,
        lineHeight: 1.4,
      },
      subtitle1: {
        fontSize: "0.98rem",
        fontWeight: 600,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: "0.9rem",
        fontWeight: 600,
        lineHeight: 1.45,
      },
      body1: {
        fontSize: "0.95rem",
        fontWeight: 500,
        lineHeight: 1.65,
      },
      body2: {
        fontSize: "0.87rem",
        fontWeight: 500,
        lineHeight: 1.6,
      },
      caption: {
        fontSize: "0.76rem",
        fontWeight: 500,
        lineHeight: 1.45,
      },
      button: {
        textTransform: "none",
        fontSize: "0.92rem",
        fontWeight: 700,
        letterSpacing: "0.01em",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: ["Outfit", "sans-serif"].join(","),
            color: "#342a31",
            backgroundColor: "#f7f4f6",
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined",
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(ownerState.color === "primary" && {
              backgroundColor: "#7b506f",
              ":hover": {
                backgroundColor: "#7b506f29",
                color: "#403e3b",
              },
            }),
            ...(ownerState.color === "secondary" && {
              backgroundColor: "#dd99bb",
              color: "#ffff",
              ":hover": {
                backgroundColor: "#7b506f29",
                color: "#403e3b",
              },
            }),
          }),
          text: {
            fontFamily: ["Outfit", "sans-serif"].join(","),
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            backgroundColor: "#7b506f",
            color: "#ffffff",
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: "#6e5b66",
            fontWeight: 600,
            fontSize: "0.88rem",
            "&.Mui-focused": {
              color: "#7b506f",
            },
            "&.Mui-error": {
              color: "#c25b6c",
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: "#6e5b66",
            fontWeight: 600,
            fontSize: "0.88rem",
            "&.Mui-focused": {
              color: "#7b506f",
            },
            "&.Mui-error": {
              color: "#c25b6c",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            backgroundColor: "#fbf8fa",
            transition: "all 0.2s ease",
            boxShadow: "inset 0 1px 2px rgba(83, 52, 69, 0.04)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(123, 80, 111, 0.18)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(123, 80, 111, 0.34)",
            },
            "&.Mui-focused": {
              backgroundColor: "#ffffff",
              boxShadow: "0 0 0 4px rgba(123, 80, 111, 0.10)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#7b506f",
              borderWidth: "1px",
            },
            "&.Mui-error": {
              backgroundColor: "#fff8f8",
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: "#d26a79",
            },
            "&.Mui-disabled": {
              backgroundColor: "#f2edf0",
            },
          },
          input: {
            padding: "12px 14px",
            fontSize: "0.92rem",
            fontWeight: 500,
            color: "#2f232a",
          },
          multiline: {
            padding: "4px 0",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            minHeight: "auto",
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            fontSize: "0.92rem",
            fontWeight: 500,
          },
          icon: {
            color: "#7b506f",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 4,
            fontWeight: 500,
            fontSize: "0.76rem",
            "&.Mui-error": {
              color: "#c25b6c",
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            "&.Mui-checked": {
              color: "#7b506f",
            },
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {},
          rounded: {},
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: "1px solid rgba(123, 80, 111, 0.18)",
            boxShadow: "0 4px 12px rgba(123, 80, 111, 0.06)",
            overflow: "hidden",
            backgroundColor: "#ffffff",
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: "#7b506f",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid rgba(123, 80, 111, 0.12)",
            padding: "12px 16px",
          },
          head: {
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "0.9rem",
            letterSpacing: "0.02em",
          },
          body: {
            color: "#4a4450",
            fontSize: "0.88rem",
            fontWeight: 500,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:nth-of-type(even)": {
              backgroundColor: "rgba(123, 80, 111, 0.03)",
            },
            "&:hover": {
              backgroundColor: "rgba(123, 80, 111, 0.08)",
            },
          },
          head: {
            "&:hover": {
              backgroundColor: "#7b506f",
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          toolbar: {
            minHeight: 52,
            color: "#4a4450",
          },
        },
      },
    },
    palette: {
      primary: {
        main: "#f5f5fc",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#e6cae0",
        contrastText: "#f5f5fc",
      },
    },
  })
);
